import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  ArrowLeft, Plus, FileText, DollarSign, CheckCircle, Clock, AlertTriangle,
  Send, Download, Eye, Trash2, MoreVertical, Calculator, Receipt,
  Building, Calendar, CreditCard, Mail, Link2, Unlink, RefreshCw, ExternalLink,
  ShieldCheck, ShieldAlert, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import HelpIcon from '@/components/HelpIcon';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fee type configurations based on Keystone Creative's pricing
const FEE_TYPES = {
  per_sf: {
    label: 'Per Square Foot',
    description: 'New Builds - $3.00/sf base rate',
    defaultRate: 3.00
  },
  percentage: {
    label: 'Percentage of Construction',
    description: 'Remodels - 7%, 10%, or 25%',
    rates: [
      { value: 7, label: '7% - Full Design Service' },
      { value: 10, label: '10% - Premium Design Service' },
      { value: 25, label: '25% - Premium Build Service' }
    ]
  },
  hourly: {
    label: 'Hourly Rate',
    description: 'Interior Design, Procurement',
    rates: [
      { value: 200, label: '$200/hr - Principal Designer' },
      { value: 175, label: '$175/hr - Interior Designer' },
      { value: 125, label: '$125/hr - Procurement' }
    ]
  },
  fixed: {
    label: 'Fixed Fee',
    description: 'Custom quoted amount'
  }
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-purple-100 text-purple-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

export default function InvoicesPage({ projectId = null, embedded = false }) {
  const darkMode = document.documentElement.classList.contains('dark');
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({ total_invoiced: 0, total_paid: 0, total_outstanding: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(projectId || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showFeeStructure, setShowFeeStructure] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendToClientInvoice, setSendToClientInvoice] = useState(null);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  
  // Accounting integration state
  const [accountingStatus, setAccountingStatus] = useState(null);
  const [showAccountingSetup, setShowAccountingSetup] = useState(false);
  const [syncingInvoice, setSyncingInvoice] = useState(null);
  const [xeroPreview, setXeroPreview] = useState(null);
  const [showXeroPreview, setShowXeroPreview] = useState(false);
  const [xeroPreviewLoading, setXeroPreviewLoading] = useState(false);

  // Check for Xero callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xero_connected') === 'true') {
      toast.success('Xero is now connected! Invoices will sync as DRAFT for your review.', { duration: 6000 });
      fetchAccountingStatus();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('xero_error')) {
      toast.error(`Xero connection failed: ${params.get('xero_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchAccountingStatus = async () => {
    try {
      const res = await axios.get(`${API}/accounting/status`);
      setAccountingStatus(res.data);
    } catch (error) {
      console.error('Error fetching accounting status:', error);
    }
  };

  const connectToXero = async () => {
    try {
      const res = await axios.get(`${API}/accounting/xero/auth-url`, {
        params: { app_origin: window.location.origin }
      });
      window.location.href = res.data.auth_url;
    } catch (error) {
      console.error('Error getting Xero auth URL:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect to Xero');
    }
  };

  const disconnectXero = async () => {
    try {
      await axios.post(`${API}/accounting/xero/disconnect`);
      setAccountingStatus({ provider: 'none', is_connected: false });
      toast.success('Disconnected from Xero');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const syncInvoiceToXero = async (invoiceId) => {
    // Step 1: Show preview first — never sync directly
    setXeroPreviewLoading(true);
    setShowXeroPreview(true);
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/xero-preview`);
      setXeroPreview(res.data);
    } catch (error) {
      toast.error('Failed to load sync preview');
      setShowXeroPreview(false);
    } finally {
      setXeroPreviewLoading(false);
    }
  };

  const confirmXeroSync = async () => {
    if (!xeroPreview) return;
    setSyncingInvoice(xeroPreview.invoice_id);
    try {
      const res = await axios.post(`${API}/invoices/${xeroPreview.invoice_id}/sync-to-xero?force=true`);
      toast.success(res.data.message);
      setShowXeroPreview(false);
      setXeroPreview(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.warnings) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Failed to sync invoice to Xero');
      }
    } finally {
      setSyncingInvoice(null);
    }
  };

  const handleSendToClient = async (invoice) => {
    if (!invoice) return;
    
    setSendingToClient(true);
    try {
      const response = await axios.post(`${API}/drawing-room/send-document`, {
        project_id: invoice.project_id,
        document_type: 'invoice',
        document_id: invoice.id,
        document_name: `Invoice #${invoice.invoice_number || invoice.id.slice(0, 8)}`,
        custom_message: customMessage || null,
        requires_signature: false,
        requires_approval: true
      });
      
      toast.success(response.data.email_sent 
        ? 'Invoice sent to client\'s Drawing Room with email notification!' 
        : 'Invoice added to client\'s Drawing Room');
      setSendToClientInvoice(null);
      setCustomMessage('');
      fetchData();
    } catch (error) {
      console.error('Error sending to client:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice to client');
    } finally {
      setSendingToClient(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAccountingStatus();
  }, [selectedProject, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedProject !== 'all') params.append('project_id', selectedProject);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const [invoicesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/invoices?${params.toString()}`),
        axios.get(`${API}/projects`)
      ]);

      setInvoices(invoicesRes.data.invoices || []);
      setSummary(invoicesRes.data.summary || {});
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background'}>
      {/* Header - Hidden when embedded */}
      {!embedded && (
        <header className="bg-card border-b border-border sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => window.location.href = '/'}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <HelpIcon elementId="invoices-page" position="inline" />
                  <div>
                    <h1 className="text-2xl font-heading">Invoices</h1>
                    <p className="text-sm text-muted-foreground">Manage client invoices and payments</p>
                  </div>
                  <BlueprintIndicator sectionId="invoices-overview" tooltip="Learn about Invoices" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Accounting Integration Button */}
                {accountingStatus?.is_connected ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2 border-green-300 bg-green-50 text-green-800 hover:bg-green-100">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                        <span className="font-medium">{accountingStatus.provider === 'xero' ? 'Xero' : 'QuickBooks'} Connected</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem disabled>
                        <Building className="w-4 h-4 mr-2" />
                        {accountingStatus.tenant_name || 'Organization'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={disconnectXero} className="text-red-600">
                        <Unlink className="w-4 h-4 mr-2" />
                        Disconnect
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button variant="outline" onClick={() => setShowAccountingSetup(true)}>
                    <Link2 className="w-4 h-4 mr-2" />
                    Connect Accounting
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowFeeStructure(true)}>
                  <Calculator className="w-4 h-4 mr-2" />
                  Fee Calculator
                </Button>
                <Button onClick={() => setShowNewInvoice(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Invoice
                </Button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Xero Connection Status Banner */}
      {accountingStatus?.is_connected && (
        <div className={embedded ? 'px-0 pb-3' : 'max-w-7xl mx-auto px-4 pt-4'}>
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200">
            <div className="w-8 h-8 bg-[#13B5EA] rounded flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">X</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-green-800">Xero Connected</span>
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              </div>
              <span className="text-xs text-green-600">{accountingStatus.tenant_name || 'Organization'}</span>
            </div>
            <span className="text-xs text-green-600">All synced invoices go to Xero as DRAFT</span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6'}>
        {/* Action buttons when embedded */}
        {embedded && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Project Invoices</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowFeeStructure(true)}>
                <Calculator className="w-4 h-4 mr-2" />
                Fee Calculator
              </Button>
              <Button size="sm" onClick={() => setShowNewInvoice(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Invoice
              </Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-xl font-semibold">{formatCurrency(summary.total_invoiced)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-xl font-semibold">{formatCurrency(summary.total_paid)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-xl font-semibold">{formatCurrency(summary.total_outstanding)}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-purple-100">
                <Receipt className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Invoice Count</p>
                <p className="text-xl font-semibold">{summary.count}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters - hide project filter when embedded */}
        <div className="flex items-center gap-4 mb-6">
          {!embedded && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoice List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No invoices yet</p>
            <Button onClick={() => setShowNewInvoice(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Invoice
            </Button>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium">Invoice #</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Project / Client</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Milestone</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Date</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Amount</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Balance</th>
                  <th className="text-center px-4 py-3 text-sm font-medium">Status</th>
                  <th className="text-center px-4 py-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">{invoice.invoice_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{invoice.project_name || 'Unknown Project'}</p>
                        <p className="text-sm text-muted-foreground">{invoice.client_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">{invoice.milestone_name || invoice.invoice_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm">{formatDate(invoice.invoice_date)}</p>
                        <p className="text-xs text-muted-foreground">Due: {formatDate(invoice.due_date)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatCurrency(invoice.total)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={invoice.balance_due > 0 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                        {formatCurrency(invoice.balance_due)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Badge className={STATUS_COLORS[invoice.status] || STATUS_COLORS.draft}>
                          {invoice.status}
                        </Badge>
                        {invoice.xero_synced && (
                          <Badge variant="outline" className="text-xs bg-[#13B5EA]/10 text-[#13B5EA] border-[#13B5EA]/30">
                            Xero
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setSelectedInvoice(invoice)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSendToClientInvoice(invoice)} data-testid={`send-invoice-${invoice.id}`}>
                            <Send className="w-4 h-4 mr-2" />
                            Send to Client
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Record Payment
                          </DropdownMenuItem>
                          {/* Xero Sync Option */}
                          {accountingStatus?.is_connected && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => syncInvoiceToXero(invoice.id)}
                                disabled={syncingInvoice === invoice.id}
                              >
                                {syncingInvoice === invoice.id ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : invoice.xero_synced ? (
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                ) : (
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                )}
                                {invoice.xero_synced ? 'Re-sync to Xero' : 'Sync to Xero'}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fee Calculator Dialog */}
      <FeeCalculatorDialog 
        open={showFeeStructure} 
        onOpenChange={setShowFeeStructure}
        projects={projects}
        onInvoiceGenerated={() => {
          fetchData();
          setShowFeeStructure(false);
        }}
      />

      {/* New Invoice Dialog */}
      <NewInvoiceDialog
        open={showNewInvoice}
        onOpenChange={setShowNewInvoice}
        projects={projects}
        onCreated={() => {
          fetchData();
          setShowNewInvoice(false);
        }}
      />

      {/* Invoice Detail Dialog */}
      {selectedInvoice && (
        <InvoiceDetailDialog
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onUpdate={fetchData}
        />
      )}

      {/* Send to Client Dialog */}
      <Dialog open={!!sendToClientInvoice} onOpenChange={(open) => !open && setSendToClientInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#70170F]" />
              Send Invoice to Client
            </DialogTitle>
          </DialogHeader>
          {sendToClientInvoice && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Invoice</p>
                <p className="font-medium">#{sendToClientInvoice.invoice_number || sendToClientInvoice.id?.slice(0, 8)}</p>
                <p className="text-sm text-muted-foreground mt-2">Amount</p>
                <p className="font-medium">{formatCurrency(sendToClientInvoice.total)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Custom Message (optional)</Label>
                <Textarea
                  placeholder="Add a personal message for the client..."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  This will be included in the email notification to the client.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <Mail className="w-4 h-4 inline mr-2" />
                  The client will receive an email notification with a link to view this invoice in their Drawing Room.
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setSendToClientInvoice(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleSendToClient(sendToClientInvoice)}
                  disabled={sendingToClient}
                  className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                  data-testid="confirm-send-to-client"
                >
                  {sendingToClient ? 'Sending...' : 'Send to Drawing Room'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accounting Integration Setup Dialog */}
      <Dialog open={showAccountingSetup} onOpenChange={setShowAccountingSetup}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect Accounting Software</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Connect KEY-MADE to your accounting software to automatically sync invoices and track payments.
            </p>
            
            <div className="space-y-3">
              {/* Xero */}
              <div className="border rounded-lg p-4 hover:border-[#70170F] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#13B5EA] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-lg">X</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Xero</h4>
                      <p className="text-sm text-muted-foreground">Cloud accounting for small business</p>
                    </div>
                  </div>
                  <Button onClick={connectToXero} data-testid="connect-xero-btn">
                    Connect
                  </Button>
                </div>
              </div>
              
              {/* QuickBooks - Coming Soon */}
              <div className="border rounded-lg p-4 opacity-60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#2CA01C] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-lg">QB</span>
                    </div>
                    <div>
                      <h4 className="font-medium">QuickBooks</h4>
                      <p className="text-sm text-muted-foreground">Small business accounting</p>
                    </div>
                  </div>
                  <Badge variant="secondary">Coming Soon</Badge>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                <strong>Setup Required:</strong> To connect Xero, you'll need to configure your Xero API credentials in the admin settings first. Contact your administrator for assistance.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Xero Sync Preview & Confirmation Dialog */}
      <Dialog open={showXeroPreview} onOpenChange={(open) => { if (!open) { setShowXeroPreview(false); setXeroPreview(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#13B5EA]" />
              Xero Sync Preview
            </DialogTitle>
          </DialogHeader>
          
          {xeroPreviewLoading ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading preview...</p>
            </div>
          ) : xeroPreview ? (
            <div className="space-y-4 py-2">
              {/* Safety Warnings */}
              {xeroPreview.has_warnings && (
                <div className={`border rounded-lg p-3 ${xeroPreview.is_blocked ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-start gap-2">
                    <ShieldAlert className={`w-5 h-5 mt-0.5 flex-shrink-0 ${xeroPreview.is_blocked ? 'text-red-600' : 'text-amber-600'}`} />
                    <div>
                      <p className={`text-sm font-medium ${xeroPreview.is_blocked ? 'text-red-800' : 'text-amber-800'}`}>
                        {xeroPreview.is_blocked ? 'Blocked — Fix required before syncing' : 'Safety Warnings — Please review'}
                      </p>
                      <ul className="mt-1 space-y-1">
                        {xeroPreview.safety_warnings.map((w, i) => (
                          <li key={i} className={`text-xs ${xeroPreview.is_blocked ? 'text-red-700' : 'text-amber-700'}`}>
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {!xeroPreview.has_warnings && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-800">All safety checks passed</p>
                </div>
              )}

              {/* Data Preview */}
              <div className="border rounded-lg p-3 space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-1">
                  <Info className="w-4 h-4" /> What Xero will receive:
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-muted-foreground">Contact:</span>
                  <span className="font-medium">{xeroPreview.xero_will_receive.contact_name}</span>
                  <span className="text-muted-foreground">Email:</span>
                  <span>{xeroPreview.xero_will_receive.contact_email || '—'}</span>
                  <span className="text-muted-foreground">Invoice #:</span>
                  <span>{xeroPreview.xero_will_receive.invoice_number || '—'}</span>
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-medium">${(xeroPreview.xero_will_receive.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  <span className="text-muted-foreground">Status in Xero:</span>
                  <span><Badge variant="secondary" className="text-xs">DRAFT</Badge></span>
                </div>

                {xeroPreview.xero_will_receive.line_items?.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Line Items:</p>
                    {xeroPreview.xero_will_receive.line_items.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs py-0.5">
                        <span className="truncate mr-2">{item.description}</span>
                        <span className="flex-shrink-0">{item.quantity} x ${(item.unit_price || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {xeroPreview.already_synced && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-800">
                  <Info className="w-3 h-3 inline mr-1" />
                  This invoice was previously synced to Xero. This will update the existing Xero draft.
                </div>
              )}

              <div className={` border rounded-lg p-2 text-xs text-muted-foreground ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                <ShieldCheck className="w-3 h-3 inline mr-1" />
                All invoices are sent to Xero as <strong>DRAFT</strong>. Nothing is finalized until you approve it inside Xero.
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowXeroPreview(false); setXeroPreview(null); }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#13B5EA] hover:bg-[#0e9bd1]"
                  onClick={confirmXeroSync}
                  disabled={xeroPreview.is_blocked || syncingInvoice}
                  data-testid="confirm-xero-sync-btn"
                >
                  {syncingInvoice ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-2" /> Confirm & Send to Xero</>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Fee Calculator Dialog Component
function FeeCalculatorDialog({ open, onOpenChange, projects, onInvoiceGenerated }) {
  const [selectedProject, setSelectedProject] = useState('');
  const [projectType, setProjectType] = useState('remodel');
  const [feeType, setFeeType] = useState('percentage');
  const [squareFootage, setSquareFootage] = useState('');
  const [constructionCost, setConstructionCost] = useState('');
  const [percentageRate, setPercentageRate] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(200);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [fixedFee, setFixedFee] = useState('');
  const [retainerPct, setRetainerPct] = useState(30);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);

  // Add-ons for new builds
  const [includeElectrical, setIncludeElectrical] = useState(false);
  const [includeDoorWindow, setIncludeDoorWindow] = useState(false);
  const [includeInteriorElev, setIncludeInteriorElev] = useState(false);
  const [includeExtMaterials, setIncludeExtMaterials] = useState(false);
  const [includeIntMaterials, setIncludeIntMaterials] = useState(false);
  const [includeCabinets, setIncludeCabinets] = useState(false);
  const [cabinetLF, setCabinetLF] = useState('');

  const calculateFees = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    try {
      setLoading(true);
      const data = {
        project_type: projectType,
        fee_type: feeType,
        square_footage: parseFloat(squareFootage) || 0,
        construction_cost: parseFloat(constructionCost) || 0,
        percentage_rate: percentageRate,
        hourly_rate: hourlyRate,
        estimated_hours: parseFloat(estimatedHours) || 0,
        fixed_fee: parseFloat(fixedFee) || 0,
        retainer_percentage: retainerPct,
        include_electrical: includeElectrical,
        include_door_window_schedule: includeDoorWindow,
        include_interior_elevations: includeInteriorElev,
        include_exterior_materials: includeExtMaterials,
        include_interior_materials: includeIntMaterials,
        include_cabinet_design: includeCabinets,
        cabinet_linear_feet: parseFloat(cabinetLF) || 0
      };

      const response = await axios.post(
        `${API}/projects/${selectedProject}/fee-structure/calculate`,
        data
      );
      setCalculation(response.data);
    } catch (error) {
      console.error('Error calculating fees:', error);
      toast.error('Failed to calculate fees');
    } finally {
      setLoading(false);
    }
  };

  const saveFeeStructure = async () => {
    if (!selectedProject || !calculation) return;

    try {
      setLoading(true);
      await axios.post(`${API}/projects/${selectedProject}/fee-structure`, {
        ...calculation,
        project_type: projectType,
        fee_type: feeType
      });
      toast.success('Fee structure saved!');
    } catch (error) {
      console.error('Error saving fee structure:', error);
      toast.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = async (milestoneName) => {
    if (!selectedProject) return;

    try {
      setLoading(true);
      // First save the fee structure
      await axios.post(`${API}/projects/${selectedProject}/fee-structure`, {
        ...calculation,
        project_type: projectType,
        fee_type: feeType
      });

      // Then generate invoice
      const response = await axios.post(
        `${API}/projects/${selectedProject}/generate-invoice`,
        { milestone: milestoneName }
      );
      toast.success(`Invoice ${response.data.invoice.invoice_number} created!`);
      onInvoiceGenerated();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Fee Calculator & Invoice Generator
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 mt-4">
          {/* Left: Configuration */}
          <div className="space-y-4">
            <div>
              <Label>Select Project</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Project Type</Label>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_build">New Build (Custom Home)</SelectItem>
                  <SelectItem value="remodel">Remodel</SelectItem>
                  <SelectItem value="interior_design">Interior Design Only</SelectItem>
                  <SelectItem value="procurement">Procurement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fee Type</Label>
              <Select value={feeType} onValueChange={setFeeType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_sf">Per Square Foot ($3.00/sf)</SelectItem>
                  <SelectItem value="percentage">% of Construction Cost</SelectItem>
                  <SelectItem value="hourly">Hourly Rate</SelectItem>
                  <SelectItem value="fixed">Fixed Fee</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Per SF inputs */}
            {feeType === 'per_sf' && (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label>Square Footage</Label>
                  <Input 
                    type="number" 
                    value={squareFootage} 
                    onChange={e => setSquareFootage(e.target.value)}
                    placeholder="e.g., 2500"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Base rate: $3.00/sf for conditioned living space</p>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium">Add-ons:</p>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeElectrical} onChange={e => setIncludeElectrical(e.target.checked)} />
                    Electrical Layout (+$0.20/sf)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeDoorWindow} onChange={e => setIncludeDoorWindow(e.target.checked)} />
                    Door & Window Schedule (+$0.20/sf)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeInteriorElev} onChange={e => setIncludeInteriorElev(e.target.checked)} />
                    Interior Elevations (+$0.20/sf)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeExtMaterials} onChange={e => setIncludeExtMaterials(e.target.checked)} />
                    Exterior Materials (+$0.50/sf)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeIntMaterials} onChange={e => setIncludeIntMaterials(e.target.checked)} />
                    Interior Materials (+$1.00/sf)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeCabinets} onChange={e => setIncludeCabinets(e.target.checked)} />
                    Custom Cabinet Design (+$25/LF)
                  </label>
                  {includeCabinets && (
                    <Input 
                      type="number" 
                      value={cabinetLF} 
                      onChange={e => setCabinetLF(e.target.value)}
                      placeholder="Linear feet of cabinets"
                      className="mt-1"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Percentage inputs */}
            {feeType === 'percentage' && (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label>Construction Cost</Label>
                  <Input 
                    type="number" 
                    value={constructionCost} 
                    onChange={e => setConstructionCost(e.target.value)}
                    placeholder="e.g., 250000"
                  />
                </div>
                <div>
                  <Label>Service Level</Label>
                  <Select value={percentageRate.toString()} onValueChange={v => setPercentageRate(parseFloat(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7% - Full Design Service</SelectItem>
                      <SelectItem value="10">10% - Premium Design Service</SelectItem>
                      <SelectItem value="25">25% - Premium Build Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Hourly inputs */}
            {feeType === 'hourly' && (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label>Hourly Rate</Label>
                  <Select value={hourlyRate.toString()} onValueChange={v => setHourlyRate(parseFloat(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="200">$200/hr - Principal Designer</SelectItem>
                      <SelectItem value="175">$175/hr - Interior Designer</SelectItem>
                      <SelectItem value="125">$125/hr - Procurement</SelectItem>
                      <SelectItem value="75">$75/hr - Draftsperson</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estimated Hours</Label>
                  <Input 
                    type="number" 
                    value={estimatedHours} 
                    onChange={e => setEstimatedHours(e.target.value)}
                    placeholder="e.g., 40"
                  />
                </div>
              </div>
            )}

            {/* Fixed fee inputs */}
            {feeType === 'fixed' && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <Label>Fixed Fee Amount</Label>
                <Input 
                  type="number" 
                  value={fixedFee} 
                  onChange={e => setFixedFee(e.target.value)}
                  placeholder="e.g., 15000"
                />
              </div>
            )}

            <div>
              <Label>Retainer Percentage</Label>
              <Select value={retainerPct.toString()} onValueChange={v => setRetainerPct(parseFloat(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                  <SelectItem value="30">30% (Standard)</SelectItem>
                  <SelectItem value="50">50%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={calculateFees} className="w-full" disabled={loading}>
              <Calculator className="w-4 h-4 mr-2" />
              Calculate Fees
            </Button>
          </div>

          {/* Right: Results */}
          <div className="space-y-4">
            {calculation ? (
              <>
                <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                  <h3 className="font-semibold">Fee Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Base Fee:</span>
                      <span className="font-medium">{formatCurrency(calculation.base_fee)}</span>
                    </div>
                    {calculation.addons_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Add-ons:</span>
                        <span className="font-medium">{formatCurrency(calculation.addons_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg border-t pt-2">
                      <span className="font-semibold">Total Fee:</span>
                      <span className="font-bold text-accent">{formatCurrency(calculation.total_fee)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Retainer ({retainerPct}%):</span>
                      <span>{formatCurrency(calculation.retainer_amount)}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                  <h3 className="font-semibold">Payment Milestones</h3>
                  <div className="space-y-2">
                    {calculation.payment_milestones?.map((milestone, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-background rounded border">
                        <div>
                          <p className="font-medium text-sm">{milestone.name}</p>
                          <p className="text-xs text-muted-foreground">{milestone.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(milestone.amount)}</p>
                          <p className="text-xs text-muted-foreground">{milestone.percentage}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={saveFeeStructure} disabled={loading} className="flex-1">
                    Save Fee Structure
                  </Button>
                </div>

                <div className="p-4 border border-accent/30 bg-accent/5 rounded-lg">
                  <h3 className="font-semibold mb-3">Generate Invoice</h3>
                  <div className="space-y-2">
                    {calculation.payment_milestones?.map((milestone, idx) => (
                      <Button 
                        key={idx}
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={() => generateInvoice(milestone.name)}
                        disabled={loading}
                      >
                        <span>{milestone.name}</span>
                        <span>{formatCurrency(milestone.amount)}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center">
                  <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Configure fee settings and click Calculate</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// New Invoice Dialog Component
function NewInvoiceDialog({ open, onOpenChange, projects, onCreated }) {
  const [selectedProject, setSelectedProject] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [lineItems, setLineItems] = useState([
    { id: '1', description: '', quantity: 1, unit: 'each', unit_price: 0, amount: 0 }
  ]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Set default due date to 30 days from now
    const due = new Date();
    due.setDate(due.getDate() + 30);
    setDueDate(due.toISOString().split('T')[0]);
  }, []);

  const updateLineItem = (id, field, value) => {
    setLineItems(items => items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit: 'each',
      unit_price: 0,
      amount: 0
    }]);
  };

  const removeLineItem = (id) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  const createInvoice = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/invoices`, {
        project_id: selectedProject,
        invoice_date: invoiceDate,
        due_date: dueDate,
        line_items: lineItems,
        notes,
        status: 'draft'
      });
      toast.success('Invoice created!');
      onCreated();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Invoice</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Project</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice Date</Label>
              <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line Items</Label>
              <Button variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {lineItems.map((item, idx) => (
                <div key={item.id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <Input 
                      placeholder="Description"
                      value={item.description}
                      onChange={e => updateLineItem(item.id, 'description', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input 
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={e => updateLineItem(item.id, 'quantity', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input 
                      type="number"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={e => updateLineItem(item.id, 'unit_price', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2 text-right font-medium">
                    ${(item.amount || 0).toFixed(2)}
                  </div>
                  <div className="col-span-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => removeLineItem(item.id)}
                      disabled={lineItems.length === 1}
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Subtotal</p>
              <p className="text-xl font-bold">${subtotal.toFixed(2)}</p>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input 
              placeholder="Additional notes for client..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={createInvoice} disabled={loading}>
              Create Invoice
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Invoice Detail Dialog
function InvoiceDetailDialog({ invoice, onClose, onUpdate }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Dialog open={!!invoice} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Invoice {invoice.invoice_number}</span>
            <Badge className={STATUS_COLORS[invoice.status]}>{invoice.status}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Bill To</p>
              <p className="font-medium">{invoice.client_name}</p>
              <p className="text-sm">{invoice.project_name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Invoice Date</p>
              <p className="font-medium">{invoice.invoice_date}</p>
              <p className="text-sm text-muted-foreground mt-2">Due Date</p>
              <p className="font-medium">{invoice.due_date}</p>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-sm">Description</th>
                  <th className="text-right px-4 py-2 text-sm">Qty</th>
                  <th className="text-right px-4 py-2 text-sm">Price</th>
                  <th className="text-right px-4 py-2 text-sm">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items?.map((item, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-4 py-2">{item.description}</td>
                    <td className="px-4 py-2 text-right">{item.quantity}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.unit_price)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal:</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              {invoice.tax_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax:</span>
                  <span>{formatCurrency(invoice.tax_amount)}</span>
                </div>
              )}
              {invoice.discount_amount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount:</span>
                  <span>-{formatCurrency(invoice.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-lg border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(invoice.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Paid:</span>
                <span className="text-green-600">{formatCurrency(invoice.amount_paid)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Balance Due:</span>
                <span className={invoice.balance_due > 0 ? 'text-amber-600' : 'text-green-600'}>
                  {formatCurrency(invoice.balance_due)}
                </span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Notes</p>
              <p className="text-sm">{invoice.notes}</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button>
              <Send className="w-4 h-4 mr-2" />
              Send to Client
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
